import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { TextFieldEvent } from '../../types/events';
import ShvkTextField from '../../styled/ShvkTextField';
import ShvkButton from '../../styled/ShvkButton';
import useStore from '../../store/storeContext';
import HelpTextDto from '../../types/helpText';
import { LocalizedName } from '../../types/localization';

interface Props {
    open: boolean;
    onClose(): void;
    helpText: HelpTextDto | null;
}

interface State {
    fi_FI: string;
    sv_SE: string;
    en_GB: string;
}

type HelpTextFormField = 'fi_FI' | 'sv_SE' | 'en_GB';

function HelpTextDialog(props: Props) {
    const { admin, localization, snackbar, theming } = useStore();

    const [state, setState] = useState<State>({
        fi_FI: '',
        sv_SE: '',
        en_GB: '',
    });

    useEffect((): void => {
        init();
    }, [props.open]);

    const handleChange = (event: TextFieldEvent, field: HelpTextFormField): void => {
        setState({
            ...state,
            [field]: event.target.value,
        });
    };

    const init = (): void => {
        setState({
            fi_FI: props.helpText?.localizedName?.fi_FI || '',
            sv_SE: props.helpText?.localizedName?.sv_SE || '',
            en_GB: props.helpText?.localizedName?.en_GB || '',
        });
    };

    async function save(): Promise<void> {
        try {
            if (props.helpText) {
                const localizedName: LocalizedName = {
                    id: props.helpText.localizedName?.id || null,
                    key: props.helpText.localizedName?.key || '',
                    fi_FI: state.fi_FI,
                    sv_SE: state.sv_SE,
                    en_GB: state.en_GB,
                };

                const params: HelpTextDto = {
                    id: props.helpText.id,
                    description: props.helpText.description,
                    location: props.helpText.location,
                    localizedName: localizedName,
                };
                await admin.updateHelpText(params);
                snackbar.showSuccess();
            }
        } catch (error) {
            snackbar.showError(error.data?.code);
        } finally {
            props.onClose();
        }
    }

    const { translate } = localization;

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            aria-labelledby="help-text-management-dialog-title"
            maxWidth={'sm'}
            fullWidth
        >
            <DialogTitle id="help-text-management-dialog-title">{translate('HELP_TEXT_MANAGEMENT')}</DialogTitle>
            <DialogContent dividers>
                <ShvkTextField
                    id="helptext-fi-textfield"
                    label={translate('FI_TRANSLATION')}
                    multiline
                    rows={5}
                    value={state.fi_FI}
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ marginBottom: theming.theme.spacing(2) }}
                    onChange={(event): void => handleChange(event, 'fi_FI')}
                    name="fi"
                />
                <ShvkTextField
                    id="helptext-se-textfield"
                    label={translate('SE_TRANSLATION')}
                    multiline
                    rows={5}
                    value={state.sv_SE}
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ marginBottom: theming.theme.spacing(2) }}
                    onChange={(event): void => handleChange(event, 'sv_SE')}
                    name="se"
                />
                <ShvkTextField
                    id="helptext-en-textfield"
                    label={translate('EN_TRANSLATION')}
                    multiline
                    rows={5}
                    value={state.en_GB}
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ marginBottom: theming.theme.spacing(2) }}
                    onChange={(event): void => handleChange(event, 'en_GB')}
                    name="en"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} variant="contained">
                    {translate('CLOSE')}
                </Button>
                <Box flexGrow={1} />
                <ShvkButton onClick={save}>{translate('SAVE')}</ShvkButton>
            </DialogActions>
        </Dialog>
    );
}

export default observer(HelpTextDialog);
